import type { Person } from '@vigeo/shared';
import { filterTags, filtersAreOn, withoutNoPlace, type ComplianceChoice, type NameSort, type RosterFilters } from './rosterFilters.js';
import { placesOf } from './staff.js';

/**
 * The Roster's map, places list, Place filter and staff heading as ONE selection (#563, Figma page 09, Concept A).
 *
 * There is no second copy of "what is selected": it is `RosterFilters.places`, the same value the Place filter writes.
 * Everything here is worked out from the people already loaded and that one filter value (hard rule 4), so a pin, a
 * place row, the filter pill and the staff heading cannot disagree.
 */

/**
 * The filters with the place selection taken off. The map's pins and counts follow THESE (search, typed areas,
 * compliance, active or inactive, phone): a count says how many of a place's people match everything else, so choosing
 * a place does not change the counts beside the other places.
 */
export const withoutPlaces = (filters: RosterFilters): RosterFilters => ({ ...withoutNoPlace(filters), places: [] });

/** Whether a filter other than the place selection is on: the counts then read "1 of 2" and a pin can be hollow. */
export const otherFiltersOn = (filters: RosterFilters): boolean => filtersAreOn(withoutPlaces(filters));

/**
 * A pin or a place row was clicked. The place becomes the ONE selected place; clicking the only selected place again
 * clears it. (With several places in the Place filter, clicking one of them narrows to that one.) "No place" goes off:
 * the list is then the people in that place. Every other filter is left as it is.
 */
export function choosePlace(filters: RosterFilters, placeId: string): RosterFilters {
  const chosen = filters.places ?? [];
  return { ...withoutNoPlace(filters), places: chosen.length === 1 && chosen[0] === placeId ? [] : [placeId] };
}

/**
 * "Show them" on the line "Not on the map: 2 staff with no place yet" (#689): the list becomes the staff with no place.
 * It takes the place of any chosen place, as choosing a place does, and every other filter is left as it is.
 */
export const showNoPlace = (filters: RosterFilters): RosterFilters => ({ ...filters, places: [], noPlace: true });

/** "Show everyone": the place selection goes ("no place" with it), and nothing else. */
export const showEveryone = (filters: RosterFilters): RosterFilters => withoutPlaces(filters);

export interface PlaceCount {
  readonly placeId: string;
  /** People on the roster who hold it and are counted (`CoverageRules.counted`). */
  readonly total: number;
  /** Of those, the people in `matching`. */
  readonly matching: number;
}

/**
 * Who a place's numbers count (#804). Left out, everyone is counted. The Roster's map counts only staff who
 * see patients (`seesPatients`): a place held only by people who are not counted is still on the map, with nobody counted there.
 */
export interface CoverageRules {
  readonly counted?: (person: Person) => boolean;
}

export interface CoverageCounts {
  /** One per place somebody on the roster holds, in no particular order. */
  readonly places: readonly PlaceCount[];
  /** Staff with no place at all (typed text from before places existed does not count as a place). */
  readonly noPlace: { readonly total: number; readonly matching: number };
  readonly staff: number;
  readonly staffWithPlace: number;
}

/**
 * How many people hold each place. `employed` is everyone on the roster; `matching` is the subset that matches the
 * filters other than the place (`withoutPlaces`). A person with two places counts once at each. A place's numbers count only the
 * people `rules.counted` keeps; the staff with no place are about the list, not the map, so they are all counted.
 */
export function coverageCounts(employed: readonly Person[], matching: readonly Person[], rules: CoverageRules = {}): CoverageCounts {
  const match = new Set(matching.map((person) => person.id));
  const counted = rules.counted ?? (() => true);
  const held = new Map<string, { total: number; matching: number }>();
  const noPlace = { total: 0, matching: 0 };
  let staffWithPlace = 0;
  for (const person of employed) {
    const places = placesOf(person);
    if (places.length === 0) {
      noPlace.total += 1;
      if (match.has(person.id)) noPlace.matching += 1;
      continue;
    }
    staffWithPlace += 1;
    const counts = counted(person);
    for (const placeId of new Set(places.map((place) => place.placeId))) {
      const found = held.get(placeId) ?? { total: 0, matching: 0 };
      held.set(placeId, found);
      if (!counts) continue;
      found.total += 1;
      if (match.has(person.id)) found.matching += 1;
    }
  }
  return { places: [...held].map(([placeId, count]) => ({ placeId, ...count })), noPlace, staff: employed.length, staffWithPlace };
}

/** The line under "Staff in Alexandria · 3" (Figma page 12, frame M2). */
export const CHOSEN_ON_THE_MAP = 'Chosen on the map';
export const NOT_ON_THE_MAP = 'Not on the map';

export interface StaffHeading {
  readonly title: string;
  readonly subtitle: string;
  /** The selected place, when the heading is about exactly one: its pin's letter is drawn beside the title. */
  readonly placeId: string | null;
  /** Whether "Show everyone" is offered: a place selection is what the list is showing. */
  readonly showEveryone: boolean;
}

/**
 * The staff list's heading (frames A1, A2 and A3; Figma page 12, frames M1 and M2): "Staff · 10", "Staff in Norfolk · 2",
 * "Staff with no place yet · 2", or "Staff · 6 of 10" with the filters that are on. It is "Staff in ..." only when the places are the whole of the Place filter: with a typed area
 * chosen as well, a person in EITHER matches, so the list is not only the people in that place.
 */
export function staffHeading(input: {
  readonly filters: RosterFilters;
  readonly shown: number;
  readonly total: number;
  readonly sort: NameSort;
  readonly placeLabelOf: (placeId: string) => string;
  readonly complianceLabel: (choice: ComplianceChoice) => string;
}): StaffHeading {
  const { filters, shown, total, sort, placeLabelOf, complianceLabel } = input;
  const places = filters.places ?? [];
  if (places.length > 0 && filters.areas.length === 0) {
    const where = places.length === 1 ? placeLabelOf(places[0]!) : `${places.length} places`;
    return {
      title: `Staff in ${where} · ${shown}`,
      subtitle: otherFiltersOn(filters) ? `${CHOSEN_ON_THE_MAP}. The other filters still apply.` : CHOSEN_ON_THE_MAP,
      placeId: places.length === 1 ? places[0]! : null,
      showEveryone: true,
    };
  }
  // The staff with no place, and nothing else of the place selection: said as what it is, with the way back.
  if (filters.noPlace === true && places.length === 0 && filters.areas.length === 0) {
    return { title: `Staff with no place yet · ${shown}`, subtitle: otherFiltersOn(filters) ? `${NOT_ON_THE_MAP}. The other filters still apply.` : NOT_ON_THE_MAP, placeId: null, showEveryone: true };
  }
  if (!filtersAreOn(filters)) return { title: `Staff · ${total}`, subtitle: `Everyone on the roster, ${sort === 'asc' ? 'A to Z' : 'Z to A'}`, placeId: null, showEveryone: false };
  return { title: `Staff · ${shown} of ${total}`, subtitle: filterTags(filters, complianceLabel, placeLabelOf).map((tag) => tag.label).join(' · '), placeId: null, showEveryone: false };
}
